/**
 * @file useLegalRevisions.ts
 * @description История редакций документа постранично (phase19.16, SB143;
 * `GET /:id/revisions?before=` с ключом `nextBefore`).
 *
 * Перечитывается, когда меняется номер действующей редакции (`liveNumber`):
 * после публикации в истории появляется новая строка. Сбой истории не
 * ломает карточку — показывается строкой с «Повторить».
 */

import { useCallback, useEffect, useState } from "react";

import { listLegalRevisions } from "../../lib/api-legal";
import { legalErrorText } from "../../lib/legal/legal-errors";
import type { LegalRevisionListItemDto } from "@chaiberi/shared";

export interface LegalRevisionsState {
  items: LegalRevisionListItemDto[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  loadMore: () => void;
  reload: () => void;
}

export function useLegalRevisions(docId: string | null, liveNumber: number | null): LegalRevisionsState {
  const [items, setItems] = useState<LegalRevisionListItemDto[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (before: number | null) => {
      if (!docId) return;
      setLoading(true);
      setError(null);
      try {
        const page = await listLegalRevisions(docId, before);
        setItems((prev) => (before === null ? page.items : [...prev, ...page.items]));
        setNextBefore(page.nextBefore);
      } catch (err) {
        setError(legalErrorText(err, "Не удалось загрузить историю редакций"));
      } finally {
        setLoading(false);
      }
    },
    [docId],
  );

  useEffect(() => {
    // Нет действующей редакции — истории нет, запрос не нужен.
    if (liveNumber === null) {
      setItems([]);
      setNextBefore(null);
      return;
    }
    void load(null);
  }, [load, liveNumber]);

  return {
    items,
    hasMore: nextBefore !== null,
    loading,
    error,
    loadMore: () => void load(nextBefore),
    reload: () => void load(null),
  };
}
